import React, { useState, useEffect } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { HiAcademicCap, HiCheck } from 'react-icons/hi2';
import { SCHEDULE_FILTER_ALL_GROUPS } from '../config/constants';

function StudentGroupSelectionModal({
    isOpen,
    onClose,
    groups = [],
    selectedGroups = [],
    onGroupSelect
}) {
    const [localSelectedGroups, setLocalSelectedGroups] = useState(selectedGroups);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            return;
        }
        const next = Array.isArray(selectedGroups)
            ? [...selectedGroups]
            : selectedGroups
                ? [selectedGroups]
                : [];
        setLocalSelectedGroups(next);
    }, [isOpen, selectedGroups]);

    const handleGroupToggle = (groupName) => {
        setLocalSelectedGroups(prev => {
            if (prev.includes(groupName)) {
                return prev.filter(g => g !== groupName);
            }
            return [groupName];
        });
    };

    const handleConfirm = () => {
        onGroupSelect(localSelectedGroups);
        onClose();
    };

    const handleCancel = () => {
        onGroupSelect([SCHEDULE_FILTER_ALL_GROUPS]);
        onClose();
    };

    const groupOptions = Array.isArray(groups) ? groups.filter(g => g) : [];

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredGroupOptions = normalizedSearch
        ? groupOptions.filter((group) => {
            const name = typeof group === 'string' ? group : (group?.name || group?.group_code || '');
            return String(name).toLowerCase().includes(normalizedSearch);
        })
        : groupOptions;

    const allGroupsRowMatchesSearch = !normalizedSearch || 'all groups'.includes(normalizedSearch);

    const isAllGroupsSelected = localSelectedGroups.includes(SCHEDULE_FILTER_ALL_GROUPS);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            title="Select Your Group"
            className="max-w-lg"
        >
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <HiAcademicCap className="h-5 w-5" />
                    <span className="text-sm">Choose your group to see your schedule</span>
                </div>

                {groupOptions.length > 0 && (
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search groups..."
                        className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                        aria-label="Search groups"
                        autoFocus
                    />
                )}

                <div className="h-64 flex flex-col rounded-lg border border-gray-100 bg-gray-50/30 overflow-hidden">
                    {allGroupsRowMatchesSearch && (
                        <div className="shrink-0 p-0.5 border-b border-gray-100/80">
                            <button
                                type="button"
                                onClick={() => handleGroupToggle(SCHEDULE_FILTER_ALL_GROUPS)}
                                className={`w-full text-left p-3 rounded-lg border-2 transition-all ${isAllGroupsSelected
                                    ? 'border-blue-500 bg-blue-50 text-blue-900'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">All Groups</span>
                                    {isAllGroupsSelected && (
                                        <HiCheck className="h-5 w-5 text-blue-600" />
                                    )}
                                </div>
                            </button>
                        </div>
                    )}
                    {groupOptions.length > 0 ? (
                        filteredGroupOptions.length > 0 ? (
                            <div className="flex-1 overflow-y-auto space-y-2 p-0.5 min-h-0">
                                {filteredGroupOptions.map((group) => {
                                    const groupName = typeof group === 'string' ? group : (group?.name || group?.group_code || 'Unknown');
                                    const groupKey = typeof group === 'string' ? group : (group?.id || group?.name || group?.group_code || 'Unknown');
                                    const isSelected = localSelectedGroups.includes(groupName);

                                    return (
                                        <button
                                            key={groupKey}
                                            type="button"
                                            onClick={() => handleGroupToggle(groupName)}
                                            className={`w-full text-left p-3 rounded-lg border-2 transition-all ${isSelected
                                                ? 'border-blue-500 bg-blue-50 text-blue-900'
                                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="font-medium">{groupName}</span>
                                                {isSelected && (
                                                    <HiCheck className="h-5 w-5 text-blue-600" />
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-center text-center px-4 min-h-0">
                                <p className="text-sm text-gray-500">No groups match your search</p>
                            </div>
                        )
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-500 px-4 text-sm">
                            <HiAcademicCap className="h-12 w-12 mb-2 text-gray-300" />
                            <p>No named groups</p>
                            <p className="mt-1 text-gray-400">Use All Groups above to see the full schedule.</p>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button
                        variant="primary"
                        onClick={handleConfirm}
                        disabled={localSelectedGroups.length === 0}
                    >
                        Select Group
                    </Button>
                </div>
            </div>
        </Modal>
    );
}

export default StudentGroupSelectionModal;
